import { deleteTourRoutes } from '@app/shared/services/route-cache.service';

// ===== DOWNLOADED TOURS STORAGE =====

const STORAGE_KEY = 'downloaded_tours';

export interface DownloadedTourInfo {
	timestamp: number;
	assets: string[];
	name?: string;
}

export interface DownloadedToursMap {
	[tourId: string]: DownloadedTourInfo;
}

/**
 * Get all downloaded tours from localStorage
 */
export function getDownloadedTours(): DownloadedToursMap {
	try {
		const data = localStorage.getItem(STORAGE_KEY);
		return data ? JSON.parse(data) : {};
	} catch {
		return {};
	}
}

/**
 * Save downloaded tours to localStorage
 */
function saveDownloadedTours(tours: DownloadedToursMap): void {
	localStorage.setItem(STORAGE_KEY, JSON.stringify(tours));
}

/**
 * Get list of all downloaded tour IDs sorted by timestamp (oldest first)
 */
export function getDownloadedTourIds(): string[] {
	const tours = getDownloadedTours();
	return Object.entries(tours)
		.sort((a, b) => a[1].timestamp - b[1].timestamp)
		.map(([tourId]) => tourId);
}

/**
 * Get the oldest downloaded tour ID
 */
export function getOldestDownloadedTourId(): string | null {
	const tourIds = getDownloadedTourIds();
	return tourIds.length > 0 ? tourIds[0] : null;
}

/**
 * Get count of downloaded tours
 */
export function getDownloadedToursCount(): number {
	return Object.keys(getDownloadedTours()).length;
}

/**
 * Check if a tour is downloaded (exists in the object)
 */
export function isTourDownloaded(tourId: string): boolean {
	migrateOldDownloadFormat(); // Auto-migrate old format if needed
	const tours = getDownloadedTours();
	return tourId in tours;
}

/**
 * Get download information for a tour
 */
export function getTourDownloadInfo(tourId: string): {
	isDownloaded: boolean;
	downloadDate: Date | null;
	assetCount: number;
} {
	const tours = getDownloadedTours();
	const info = tours[tourId];

	return {
		isDownloaded: !!info,
		downloadDate: info?.timestamp ? new Date(info.timestamp) : null,
		assetCount: info?.assets?.length || 0,
	};
}

/**
 * Check if a tour's download is stale (older than 7 days)
 */
export function isTourDownloadStale(tourId: string): boolean {
	const tours = getDownloadedTours();
	const info = tours[tourId];
	if (!info?.timestamp) return false;
	const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
	return Date.now() - info.timestamp > sevenDaysMs;
}

/**
 * Mark a tour as downloaded with its assets
 */
export function markTourAsDownloaded(tourId: string, assets: string[], name?: string): void {
	const tours = getDownloadedTours();
	tours[tourId] = {
		timestamp: Date.now(),
		assets,
		name,
	};
	saveDownloadedTours(tours);
	console.log(`[DownloadedTours] Marked tour ${tourId} as downloaded (${assets.length} assets)`);
}

/**
 * Remove a tour from downloaded tours
 */
export function removeTourFromDownloaded(tourId: string): void {
	const tours = getDownloadedTours();
	delete tours[tourId];
	saveDownloadedTours(tours);
	console.log(`[DownloadedTours] Removed tour ${tourId} from downloaded tours`);
}

/**
 * Get assets for a specific tour
 */
export function getTourAssets(tourId: string): string[] {
	const tours = getDownloadedTours();
	return tours[tourId]?.assets || [];
}

// ===== MIGRATION FROM OLD FORMAT =====

/**
 * Migrate from old localStorage format to new format
 * Old: downloaded_done{tourId}, download_timestamp_{tourId}, tour_assets_{tourId}
 * New: downloaded_tours = { tourId: { timestamp, assets } }
 */
export function migrateOldDownloadFormat(): void {
	const existingData = localStorage.getItem(STORAGE_KEY);
	if (existingData) {
		return; // Already migrated
	}

	const tours: DownloadedToursMap = {};
	const keysToRemove: string[] = [];

	for (let i = 0; i < localStorage.length; i++) {
		const key = localStorage.key(i);
		if (!key) continue;

		if (key.startsWith('downloaded_done')) {
			const tourId = key.replace('downloaded_done', '');
			const status = localStorage.getItem(key);
			const timestamp = localStorage.getItem(`download_timestamp_${tourId}`);
			const assetsStr = localStorage.getItem(`tour_assets_${tourId}`);

			if (status === 'done') {
				tours[tourId] = {
					timestamp: timestamp ? parseInt(timestamp) : Date.now(),
					assets: assetsStr ? JSON.parse(assetsStr) : [],
				};
			}

			keysToRemove.push(key);
			keysToRemove.push(`download_timestamp_${tourId}`);
			keysToRemove.push(`tour_assets_${tourId}`);
		}
	}

	if (Object.keys(tours).length > 0) {
		saveDownloadedTours(tours);
		console.log(`[Migration] Migrated ${Object.keys(tours).length} tours to new format`);
		keysToRemove.forEach(key => localStorage.removeItem(key));
	}
}

// ===== TOUR CACHE DELETION =====

/**
 * Delete cached assets for a specific tour
 */
export async function deleteTourCache(tourId: string): Promise<boolean> {
	try {
		console.log(`[DeleteCache] Clearing cache for tour: ${tourId}`);

		const assets = getTourAssets(tourId);

		if (assets.length === 0) {
			console.log(`[DeleteCache] No assets found for tour ${tourId}`);
			removeTourFromDownloaded(tourId);
			return false;
		}

		if ('caches' in window) {
			const assetCache = await caches.open('firebase-storage-cache');
			const apiCache = await caches.open('firebase-api-cache');
			let deletedCount = 0;

			for (const url of assets) {
				const cleanUrl = url.split('&token=')?.[0];
				const deleted = await assetCache.delete(cleanUrl);
				if (deleted) deletedCount++;
			}

			// Delete tour API responses
			const getTourUrl = `https://europe-central2-tours-app-1579553856346.cloudfunctions.net/getTour?tourId=${tourId}`;
			const getAllAssUrl = `https://europe-west6-tours-app-1579553856346.cloudfunctions.net/getAllAss/?tourID=${tourId}`;
			await apiCache.delete(getTourUrl);
			await apiCache.delete(getAllAssUrl);

			await deleteTourRoutes(tourId);
			removeTourFromDownloaded(tourId);

			console.log(`[DeleteCache] Deleted ${deletedCount}/${assets.length} cached assets for tour ${tourId}`);
			return deletedCount > 0;
		}

		return false;
	} catch (error) {
		console.error(`[DeleteCache] Error deleting cache for tour ${tourId}:`, error);
		throw error;
	}
}

/**
 * Delete the oldest downloaded tour
 */
export async function deleteOldestTour(): Promise<string | null> {
	const oldestTourId = getOldestDownloadedTourId();
	if (oldestTourId) {
		await deleteTourCache(oldestTourId);
		return oldestTourId;
	}
	return null;
}

/**
 * Ensure there's room for a new tour by deleting oldest if at limit
 */
export async function ensureRoomForNewTour(maxTours: number = 2): Promise<void> {
	const currentCount = getDownloadedToursCount();
	if (currentCount >= maxTours) {
		const deleted = await deleteOldestTour();
		console.log(`[DeleteCache] Deleted oldest tour ${deleted} to make room`);
	}
}

// ===== GLOBAL DELETION FUNCTIONS =====

export async function deleteAllDatabases() {
	try {
		const dbs = await (window.indexedDB).databases() as { name: string }[];
		for (const db of dbs) {
			await closeConnections(db.name);
			await deleteDatabase(db.name);
		}
	} catch (error) {
		console.error('Error deleting databases:', error);
	}
}

function closeConnections(dbName: string) {
	return new Promise<void>((resolve, reject) => {
		const request = window.indexedDB.open(dbName);
		request.onsuccess = (event) => {
			(event as any).target.result.close();
			resolve();
		};
		request.onerror = (event) => reject(event);
	});
}

function deleteDatabase(dbName: string) {
	return new Promise<void>((resolve, reject) => {
		const request = window.indexedDB.deleteDatabase(dbName);
		request.onsuccess = () => resolve();
		request.onerror = (event) => reject(event);
		request.onblocked = () => reject(new Error(`Blocked deletion of ${dbName}`));
	});
}

export async function deleteAllCachesWithRetry(retryDelay = 1000, maxRetries = 3) {
	const cacheNames = await caches.keys();
	for (const cacheName of cacheNames) {
		await deleteCacheWithRetry(cacheName, retryDelay, maxRetries);
	}
}

function deleteCacheWithRetry(cacheName: string, retryDelay = 1000, maxRetries = 3) {
	return new Promise<void>((resolve, reject) => {
		let attempts = 0;

		async function attemptDelete() {
			const deleted = await caches.delete(cacheName);
			if (deleted) {
				resolve();
			} else if (attempts < maxRetries) {
				attempts++;
				setTimeout(attemptDelete, retryDelay);
			} else {
				reject(new Error(`Failed to delete cache ${cacheName}`));
			}
		}

		attemptDelete();
	});
}
